import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BranchViewProvider } from "@/context/BranchViewContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import DemoSignup from "@/pages/DemoSignup";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import Teams from "@/pages/Teams";
import TeamDetail from "@/pages/TeamDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import Attendance from "@/pages/Attendance";
import Workload from "@/pages/Workload";
import RiskCenter from "@/pages/RiskCenter";
import GitAnalytics from "@/pages/GitAnalytics";
import Leaderboards from "@/pages/Leaderboards";
import Meetings from "@/pages/Meetings";
import Reports from "@/pages/Reports";
import DeveloperApis from "@/pages/DeveloperApis";
import AIManager from "@/pages/AIManager";
import Settings from "@/pages/Settings";
import Repositories from "@/pages/Repositories";
import Chat from "@/pages/Chat";
import Branches from "@/pages/Branches";
import Clients from "@/pages/Clients";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Holidays from "@/pages/Holidays";
import Leaves from "@/pages/Leaves";
import Backlog from "@/pages/Backlog";
import Sprints from "@/pages/Sprints";
import Epics from "@/pages/Epics";
import EpicDetail from "@/pages/EpicDetail";
import Features from "@/pages/Features";
import FeatureDetail from "@/pages/FeatureDetail";
import Documentation from "@/pages/Documentation";
import DocumentDetail from "@/pages/DocumentDetail";
import ScrollToTop from "@/components/ScrollToTop";

// Route guard: only admins can access. Employees are redirected home.
// `allowBranchAdmin` additionally lets Branch Admins in (e.g. Clients).
function AdminOnly({ children, allowBranchAdmin = false }) {
  const { user } = useAuth();
  const ok = user && (user.role === "admin" || (allowBranchAdmin && user.role === "branch_admin"));
  if (user && !ok) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BranchViewProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/demo" element={<DemoSignup />} />
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/employees/:id" element={<EmployeeProfile />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/teams/:id" element={<TeamDetail />} />
              <Route path="/clients" element={<AdminOnly allowBranchAdmin><Clients /></AdminOnly>} />
              <Route path="/branches" element={<AdminOnly><Branches /></AdminOnly>} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/backlog" element={<Backlog />} />
              <Route path="/sprints" element={<Sprints />} />
              <Route path="/epics" element={<Epics />} />
              <Route path="/epics/:id" element={<EpicDetail />} />
              <Route path="/features" element={<Features />} />
              <Route path="/features/:id" element={<FeatureDetail />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/documentation/:id" element={<DocumentDetail />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/leaves" element={<Leaves />} />
              <Route path="/holidays" element={<AdminOnly allowBranchAdmin><Holidays /></AdminOnly>} />
              <Route path="/workload" element={<Workload />} />
              <Route path="/risks" element={<RiskCenter />} />
              <Route path="/git" element={<GitAnalytics />} />
              <Route path="/repositories" element={<Repositories />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/meetings" element={<Meetings />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/developer-apis" element={<AdminOnly><DeveloperApis /></AdminOnly>} />
              <Route path="/ai" element={<AIManager />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </BranchViewProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
